import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit2, Trash2, Download, Layers, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableItem } from '../components/SortableItem';
import { AddGradeDialog } from './AddGradeDialog';
import { classificationLabel } from './constants';
import { EditGradeDialog } from './EditGradeDialog';
import { GradePickerDialog } from '../GradePickerDialog';
import { emptyGradeForm } from './constants';

export function GradesTab({ sensors, currentUser, schedules: passedSchedules }) {
  const [grades, setGrades] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [figures, setFigures] = useState([]);
  const [dances, setDances] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  // Pending Event-Timing payload collected by GradeEventTimingSection
  // in edit mode. Flushed to the server as a second request right
  // after the grade PATCH succeeds (owner spec Feb 2026: Save button
  // writes both the grade row AND its event_timings row).
  const [pendingTimingUpdate, setPendingTimingUpdate] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [appendPickerOpen, setAppendPickerOpen] = useState(false);
  
  // Filter states
  const [nameFilter, setNameFilter] = useState('');
  const [scheduleFilter, setScheduleFilter] = useState('all');
  const [judgingFilter, setJudgingFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  
  const isSuperUser = currentUser?.role === 'super';
  const currentUserId = currentUser?.user_id;
  
  // Check if user can edit a specific grade
  const canEditItem = (item) => {
    return isSuperUser || item.created_by === currentUserId;
  };
  
  // Shared form data for Add mode (discipline and schedule)
  const [sharedData, setSharedData] = useState({
    discipline_id: '',
    schedule_id: ''
  });
  
  // Multiple grade forms for Add mode (up to 3)
  const [gradeForms, setGradeForms] = useState([
    { ...emptyGradeForm },
    { ...emptyGradeForm }
  ]);
  
  const fetchGrades = async () => {
    try {
      const res = await axios.get(`${API}/content/grades`, { withCredentials: true });
      setGrades(res.data);
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };
  
  const fetchDisciplines = async () => {
    try {
      const res = await axios.get(`${API}/content/disciplines`, { withCredentials: true });
      setDisciplines(res.data);
    } catch (error) {
      console.error('Error fetching disciplines:', error);
    }
  };
  
  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/content/schedules`, { withCredentials: true });
      setSchedules(res.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };
  
  const fetchFigures = async () => {
    try {
      const res = await axios.get(`${API}/content/figures`, { withCredentials: true });
      setFigures(res.data);
    } catch (error) {
      console.error('Error fetching figures:', error);
    }
  };
  
  const fetchDances = async () => {
    try {
      const res = await axios.get(`${API}/content/compulsory-dances`, { withCredentials: true });
      setDances(res.data);
    } catch (error) {
      console.error('Error fetching dances:', error);
    }
  };
  
  useEffect(() => {
    fetchGrades();
    fetchDisciplines();
    fetchSchedules();
    fetchFigures();
    fetchDances();
  }, []);
  
  // Normalize text for accent-insensitive search (e.g., "Whetu" matches "Whetū")
  const normalizeText = (text) => {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };
  
  // Filter grades based on name, schedule and judging-system filters
  const filteredGrades = grades.filter(grade => {
    const discipline = disciplines.find(d => d.discipline_id === grade.discipline_id);
    const displayName = `${grade.name} ${discipline?.discipline || ''}`.trim();
    
    // Name filter (accent-insensitive)
    const nameMatch = !nameFilter || 
      normalizeText(displayName).includes(normalizeText(nameFilter));
    
    // Schedule filter
    const scheduleMatch = scheduleFilter === 'all' || 
      grade.schedule_id === scheduleFilter;

    // Judging system filter (default-safe: missing field treated as RollArt)
    const gradeJudging = grade.judging_system || 'RollArt';
    const judgingMatch = judgingFilter === 'all' || gradeJudging === judgingFilter;

    return nameMatch && scheduleMatch && judgingMatch;
  });
  
  // Export grades to Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/content/grades/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'grades_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Grades exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export grades');
    } finally {
      setExporting(false);
    }
  };
  
  const resetAddForm = () => {
    setSharedData({ discipline_id: '', schedule_id: '' });
    setGradeForms([
      { ...emptyGradeForm },
      { ...emptyGradeForm }
    ]);
  };
  
  const updateGradeForm = (index, field, value) => {
    setGradeForms(prevForms => {
      const updated = [...prevForms];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  const updateGradeFormGroup = (index, groupNum, field, value) => {
    setGradeForms(prevForms => {
      const updated = [...prevForms];
      updated[index] = {
        ...updated[index],
        [`group_${groupNum}`]: {
          ...updated[index][`group_${groupNum}`],
          [field]: value
        }
      };
      return updated;
    });
  };
  
  // Format figure display with foot notation
  const formatFigureDisplay = (figure, selectedFoot) => {
    if (!figure) return '';
    const displayNum = figure.display_number ? `${figure.number}${figure.display_number}` : figure.number;
    let desc = figure.description;
    
    if (selectedFoot === 'Right' || selectedFoot === 'R') {
      desc = desc.replace(/\bFO\b/g, 'RFO').replace(/\bFI\b/g, 'RFI').replace(/\bBO\b/g, 'RBO').replace(/\bBI\b/g, 'RBI');
      desc = desc.replace(/\bLFO\b/g, 'LFO').replace(/\bLFI\b/g, 'LFI').replace(/\bLBO\b/g, 'LBO').replace(/\bLBI\b/g, 'LBI');
      desc = desc.replace(/RFO-RFO/g, 'RFO-LFO').replace(/RFI-RFI/g, 'RFI-LFI').replace(/RBO-RBO/g, 'RBO-LBO').replace(/RBI-RBI/g, 'RBI-LBI');
    } else if (selectedFoot === 'Left' || selectedFoot === 'L') {
      desc = desc.replace(/\bFO\b/g, 'LFO').replace(/\bFI\b/g, 'LFI').replace(/\bBO\b/g, 'LBO').replace(/\bBI\b/g, 'LBI');
      desc = desc.replace(/LFO-LFO/g, 'LFO-RFO').replace(/LFI-LFI/g, 'LFI-RFI').replace(/LBO-LBO/g, 'LBO-RBO').replace(/LBI-LBI/g, 'LBI-RBI');
    }
    
    return `${displayNum} – ${desc}`;
  };
  
  const handleAddSubmit = async (e, keepOpen = false) => {
    e.preventDefault();
    
    if (!sharedData.discipline_id || !sharedData.schedule_id) {
      toast.error('Please select Discipline and Schedule');
      return;
    }
    
    const filledGrades = gradeForms.filter((form, idx) => {
      if (idx === 0) return true;
      return form.name.trim() !== '';
    });
    
    if (!filledGrades[0].name.trim()) {
      toast.error('Please enter a name for Grade 1');
      return;
    }
    
    for (let i = 0; i < filledGrades.length; i++) {
      const form = filledGrades[i];
      const gradeNum = gradeForms.indexOf(form) + 1;
      
      if (!form.round_one_content_type) {
        toast.error(`Please select Round One Content Type for Grade ${gradeNum}`);
        return;
      }
      if (form.round_one_content_type === 'Competition Specific Event' && !form.judges_notes_round_one) {
        toast.error(`Judges Notes Round One is required for Competition Specific Event in Grade ${gradeNum}`);
        return;
      }
      if (form.round_one_content_type === 'Groups') {
        if (!form.num_groups) {
          toast.error(`Please select Number of Groups for Grade ${gradeNum}`);
          return;
        }
        if (!form.group_1.figure_1_id) {
          toast.error(`Please select at least the first figure in Group 1 for Grade ${gradeNum}`);
          return;
        }
      }
      if (form.round_one_content_type === 'Compulsories' && !form.dance_1_id) {
        toast.error(`Please select Dance 1 for Grade ${gradeNum}`);
        return;
      }
    }
    
    try {
      const gradesToCreate = filledGrades.map(form => {
        const gradeData = {
          discipline_id: sharedData.discipline_id,
          schedule_id: sharedData.schedule_id,
          name: form.name,
          classification: form.classification,
          judging_system: form.judging_system,
          group_by_grade: form.group_by_grade,
          is_beginner_for_fees: form.is_beginner_for_fees,
          b_mark_no_rollart: form.b_mark_no_rollart,
          local_regional_only: form.local_regional_only,
          skate_mf_together: form.skate_mf_together,
          judge_mf_together: form.judge_mf_together,
          can_use_panels: form.can_use_panels,
          round_one_content_type: form.round_one_content_type,
          round_two_content_type: form.round_two_content_type,
          judges_notes_round_one: form.judges_notes_round_one,
          judges_notes_round_two: form.judges_notes_round_two,
          include_judges_notes_in_schedule: form.include_judges_notes_in_schedule,
          num_groups: form.num_groups,
          circuits_per_figure: form.circuits_per_figure,
          groups: null,
          compulsories: null,
          round_one_timing_type: form.round_one_timing_type,
          round_one_add_margin: form.round_one_add_margin,
          round_one_time_min: form.round_one_time_min || null,
          round_one_time_max: form.round_one_time_max || null,
          round_one_margin: form.round_one_margin || null,
          round_one_styledance_id: form.round_one_styledance_id || null,
          round_two_timing_type: form.round_two_timing_type,
          round_two_add_margin: form.round_two_add_margin,
          round_two_time_min: form.round_two_time_min || null,
          round_two_time_max: form.round_two_time_max || null,
          round_two_margin: form.round_two_margin || null,
          round_two_styledance_id: form.round_two_styledance_id || null,
          min_skaters: form.min_skaters === '' || form.min_skaters == null ? 4 : parseInt(form.min_skaters, 10),
          max_skaters: form.max_skaters === '' || form.max_skaters == null ? null : parseInt(form.max_skaters, 10),
          max_reserves: form.max_reserves === '' || form.max_reserves == null ? null : parseInt(form.max_reserves, 10)
        };
        
        if (form.round_one_content_type === 'Groups' && form.num_groups) {
          const groupsArray = [];
          for (let i = 1; i <= form.num_groups; i++) {
            const group = form[`group_${i}`];
            if (group) {
              groupsArray.push({
                group_number: i,
                figure_1_id: group.figure_1_id || null,
                figure_1_foot: group.figure_1_foot || null,
                figure_2_id: group.figure_2_id || null,
                figure_2_foot: group.figure_2_foot || null,
                figure_3_id: group.figure_3_id || null,
                figure_3_foot: group.figure_3_foot || null,
                figure_4_id: group.figure_4_id || null,
                figure_4_foot: group.figure_4_foot || null
              });
            }
          }
          gradeData.groups = groupsArray;
        }
        
        if (form.round_one_content_type === 'Compulsories') {
          gradeData.compulsories = {
            dance_1_id: form.dance_1_id || null,
            dance_2_id: form.dance_2_id || null
          };
        }
        
        return gradeData;
      });
      
      await axios.post(`${API}/content/grades/bulk`, gradesToCreate, { withCredentials: true });
      toast.success(`${gradesToCreate.length} grade(s) created successfully!`);
      
      if (keepOpen) {
        const currentDiscipline = sharedData.discipline_id;
        const currentSchedule = sharedData.schedule_id;
        setGradeForms([
          { ...emptyGradeForm },
          { ...emptyGradeForm }
        ]);
        setSharedData({ discipline_id: currentDiscipline, schedule_id: currentSchedule });
      } else {
        setAddDialogOpen(false);
        resetAddForm();
      }
      fetchGrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create grades');
      console.error(error);
    }
  };
  
  const handleEditSubmit = async (e, loadNext = false) => {
    e.preventDefault();
    if (!editingGrade) return;
    
    try {
      const form = gradeForms[0];
      const updateData = {
        discipline_id: sharedData.discipline_id,
        schedule_id: sharedData.schedule_id,
        name: form.name,
        classification: form.classification,
        judging_system: form.judging_system,
        group_by_grade: form.group_by_grade,
        is_beginner_for_fees: form.is_beginner_for_fees,
        b_mark_no_rollart: form.b_mark_no_rollart,
        local_regional_only: form.local_regional_only,
        skate_mf_together: form.skate_mf_together,
        judge_mf_together: form.judge_mf_together,
        can_use_panels: form.can_use_panels,
        round_one_content_type: form.round_one_content_type,
        round_two_content_type: form.round_two_content_type,
        judges_notes_round_one: form.judges_notes_round_one,
        judges_notes_round_two: form.judges_notes_round_two,
        include_judges_notes_in_schedule: form.include_judges_notes_in_schedule,
        num_groups: form.num_groups,
        circuits_per_figure: form.circuits_per_figure,
        groups: null,
        compulsories: null,
        round_one_timing_type: form.round_one_timing_type,
        round_one_add_margin: form.round_one_add_margin,
        round_one_time_min: form.round_one_time_min || null,
        round_one_time_max: form.round_one_time_max || null,
        round_one_margin: form.round_one_margin || null,
        round_one_styledance_id: form.round_one_styledance_id || null,
        round_two_timing_type: form.round_two_timing_type,
        round_two_add_margin: form.round_two_add_margin,
        round_two_time_min: form.round_two_time_min || null,
        round_two_time_max: form.round_two_time_max || null,
        round_two_margin: form.round_two_margin || null,
        round_two_styledance_id: form.round_two_styledance_id || null,
        min_skaters: form.min_skaters === '' || form.min_skaters == null ? 4 : parseInt(form.min_skaters, 10),
        max_skaters: form.max_skaters === '' || form.max_skaters == null ? null : parseInt(form.max_skaters, 10),
        max_reserves: form.max_reserves === '' || form.max_reserves == null ? null : parseInt(form.max_reserves, 10)
      };
      
      if (form.round_one_content_type === 'Groups' && form.num_groups) {
        const groupsArray = [];
        for (let i = 1; i <= form.num_groups; i++) {
          const group = form[`group_${i}`];
          if (group) {
            groupsArray.push({
              group_number: i,
              figure_1_id: group.figure_1_id || null,
              figure_1_foot: group.figure_1_foot || null,
              figure_2_id: group.figure_2_id || null,
              figure_2_foot: group.figure_2_foot || null,
              figure_3_id: group.figure_3_id || null,
              figure_3_foot: group.figure_3_foot || null,
              figure_4_id: group.figure_4_id || null,
              figure_4_foot: group.figure_4_foot || null
            });
          }
        }
        updateData.groups = groupsArray;
      }
      
      if (form.round_one_content_type === 'Compulsories') {
        updateData.compulsories = {
          dance_1_id: form.dance_1_id || null,
          dance_2_id: form.dance_2_id || null
        };
      }
      
      await axios.patch(`${API}/content/grades/${editingGrade.grade_id}`, updateData, { withCredentials: true });

      // Second save: event_timings row + any schedule-wide fields
      // the user flipped in the Event Timing section. Non-fatal — a
      // transport hiccup here should surface a toast but still let
      // the grade save count as successful.
      if (pendingTimingUpdate) {
        try {
          await axios.put(
            `${API}/event-timings/by-grade/${editingGrade.grade_id}`,
            pendingTimingUpdate,
            { withCredentials: true }
          );
        } catch (timingErr) {
          console.error('Error saving timings:', timingErr);
          toast.error(timingErr.response?.data?.detail || 'Grade saved, but timings failed to save');
        }
        setPendingTimingUpdate(null);
      }

      toast.success('Grade updated!');
      
      if (loadNext) {
        const freshGradesRes = await axios.get(`${API}/content/grades`, { withCredentials: true });
        const freshGrades = freshGradesRes.data;
        setGrades(freshGrades);
        
        // Walker order: mirror what the user currently sees on the Grades
        // tab — respect the schedule / judging / name filters, then walk
        // in schedule order (from the Schedules tab in Content Library)
        // and grade sort_order within each schedule. So all "Rising Star"
        // grades finish before jumping to the next schedule.
        const scheduleSortById = Object.fromEntries(
          schedules.map(s => [s.schedule_id, s.sort_order ?? 0])
        );
        const walkable = freshGrades
          .filter(g => {
            const discipline = disciplines.find(d => d.discipline_id === g.discipline_id);
            const displayName = `${g.name} ${discipline?.discipline || ''}`.trim();
            const nameMatch = !nameFilter ||
              normalizeText(displayName).includes(normalizeText(nameFilter));
            const scheduleMatch = scheduleFilter === 'all' || g.schedule_id === scheduleFilter;
            const gradeJudging = g.judging_system || 'RollArt';
            const judgingMatch = judgingFilter === 'all' || gradeJudging === judgingFilter;
            return nameMatch && scheduleMatch && judgingMatch;
          })
          .sort((a, b) => {
            const sa = scheduleSortById[a.schedule_id] ?? Number.MAX_SAFE_INTEGER;
            const sb = scheduleSortById[b.schedule_id] ?? Number.MAX_SAFE_INTEGER;
            if (sa !== sb) return sa - sb;
            return (a.sort_order || 0) - (b.sort_order || 0);
          });
        const currentIndex = walkable.findIndex(g => g.grade_id === editingGrade.grade_id);
        const nextGrade = currentIndex >= 0 ? walkable[currentIndex + 1] : null;
        
        if (nextGrade) {
          await handleEdit(nextGrade);
          // Get discipline name for full grade display
          const disciplineName = disciplines.find(d => d.discipline_id === nextGrade.discipline_id)?.discipline || '';
          toast.info(`Now editing: ${nextGrade.name} ${disciplineName}`);
        } else {
          toast.info('No more grades to edit');
          setEditDialogOpen(false);
          setEditingGrade(null);
        }
      } else {
        await fetchGrades();
        setEditDialogOpen(false);
        setEditingGrade(null);
      }
    } catch (error) {
      toast.error('Failed to update grade');
    }
  };
  
  const handleEdit = async (grade) => {
    setViewOnly(false);
    try {
      const response = await axios.get(`${API}/content/grades/${grade.grade_id}`, { withCredentials: true });
      const gradeDetail = response.data;
      
      setSharedData({
        discipline_id: gradeDetail.discipline_id,
        schedule_id: gradeDetail.schedule_id
      });
      
      const formData = {
        grade_id: gradeDetail.grade_id,
        name: gradeDetail.name,
        classification: gradeDetail.classification,
        judging_system: gradeDetail.judging_system || 'RollArt',
        group_by_grade: gradeDetail.group_by_grade,
        is_beginner_for_fees: gradeDetail.is_beginner_for_fees ?? false,
        b_mark_no_rollart: gradeDetail.b_mark_no_rollart,
        local_regional_only: gradeDetail.local_regional_only,
        skate_mf_together: gradeDetail.skate_mf_together,
        judge_mf_together: gradeDetail.judge_mf_together,
        can_use_panels: gradeDetail.can_use_panels,
        round_one_content_type: gradeDetail.round_one_content_type,
        round_two_content_type: gradeDetail.round_two_content_type,
        judges_notes_round_one: gradeDetail.judges_notes_round_one || '',
        judges_notes_round_two: gradeDetail.judges_notes_round_two || '',
        include_judges_notes_in_schedule: gradeDetail.include_judges_notes_in_schedule,
        num_groups: gradeDetail.num_groups,
        circuits_per_figure: gradeDetail.circuits_per_figure || '2 per figure',
        group_1: { ...emptyGradeForm.group_1 },
        group_2: { ...emptyGradeForm.group_2 },
        group_3: { ...emptyGradeForm.group_3 },
        group_4: { ...emptyGradeForm.group_4 },
        dance_1_id: '',
        dance_2_id: '',
        round_one_timing_type: gradeDetail.round_one_timing_type || 'maximum',
        round_one_add_margin: gradeDetail.round_one_add_margin || false,
        round_one_time_min: gradeDetail.round_one_time_min || '',
        round_one_time_max: gradeDetail.round_one_time_max || '',
        round_one_margin: gradeDetail.round_one_margin || '',
        round_one_styledance_id: gradeDetail.round_one_styledance_id || '',
        round_two_timing_type: gradeDetail.round_two_timing_type || 'maximum',
        round_two_add_margin: gradeDetail.round_two_add_margin || false,
        round_two_time_min: gradeDetail.round_two_time_min || '',
        round_two_time_max: gradeDetail.round_two_time_max || '',
        round_two_margin: gradeDetail.round_two_margin || '',
        round_two_styledance_id: gradeDetail.round_two_styledance_id || '',
        min_skaters: gradeDetail.min_skaters == null ? 4 : gradeDetail.min_skaters,
        max_skaters: gradeDetail.max_skaters == null ? '' : gradeDetail.max_skaters,
        max_reserves: gradeDetail.max_reserves == null ? '' : gradeDetail.max_reserves
      };
      
      if (gradeDetail.groups && gradeDetail.groups.length > 0) {
        for (const group of gradeDetail.groups) {
          formData[`group_${group.group_number}`] = {
            figure_1_id: group.figure_1_id || '',
            figure_1_foot: group.figure_1_foot || '',
            figure_2_id: group.figure_2_id || '',
            figure_2_foot: group.figure_2_foot || '',
            figure_3_id: group.figure_3_id || '',
            figure_3_foot: group.figure_3_foot || '',
            figure_4_id: group.figure_4_id || '',
            figure_4_foot: group.figure_4_foot || ''
          };
        }
      }
      
      if (gradeDetail.compulsories) {
        formData.dance_1_id = gradeDetail.compulsories.dance_1_id || '';
        formData.dance_2_id = gradeDetail.compulsories.dance_2_id || '';
      }
      
      setGradeForms([formData]);
      setEditingGrade(gradeDetail);
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error fetching grade details:', error);
      toast.error('Failed to load grade details');
    }
  };
  
  const handleDeleteClick = (gradeId) => {
    const grade = grades.find(g => g.grade_id === gradeId);
    if (!canEditItem(grade)) {
      toast.error('You can only delete grades you created');
      return;
    }
    setItemToDelete(grade);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/content/grades/${itemToDelete.grade_id}`, { withCredentials: true });
      toast.success('Grade deleted');
      fetchGrades();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Permission denied');
      } else {
        toast.error('Failed to delete grade');
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };
  
  const handleGradeDragEnd = async (event) => {
    if (!isSuperUser) {
      toast.error('Only Super Users can reorder grades');
      return;
    }
    
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = grades.findIndex(g => g.grade_id === active.id);
    const newIndex = grades.findIndex(g => g.grade_id === over.id);
    const newOrder = arrayMove(grades, oldIndex, newIndex);
    setGrades(newOrder);
    
    try {
      await axios.post(`${API}/content/grades/reorder`, 
        newOrder.map(g => g.grade_id), 
        { withCredentials: true }
      );
      toast.success('Order updated');
    } catch (error) {
      toast.error('Failed to update order');
      fetchGrades();
    }
  };
  
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Grades</h2>
        <div className="flex gap-2">
          <Button 
            onClick={handleExport} 
            variant="outline" 
            disabled={exporting}
            data-testid="export-grades-button"
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
          {scheduleFilter && scheduleFilter !== 'all' && (
            <Button
              onClick={() => setAppendPickerOpen(true)}
              variant="outline"
              data-testid="append-grades-button"
              title="Add grades from other schedules to the currently-filtered schedule"
            >
              <Layers className="mr-2 h-4 w-4" />
              Add from other schedules
            </Button>
          )}
          <Button onClick={() => { resetAddForm(); setAddDialogOpen(true); }} className="btn-primary" data-testid="add-grade-button">
            <Plus className="mr-2 h-4 w-4" />
            Add Grade
          </Button>
        </div>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create new grades and edit/delete your own items. Items created by others are view-only.
        </div>
      )}
      
      <Card className="p-6">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGradeDragEnd}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="w-8"></th>
                  <th className="text-left py-3 px-2 font-semibold">Name</th>
                  <th className="text-left py-3 px-2 font-semibold">Schedule</th>
                  <th className="text-center py-3 px-2 font-semibold">Classification</th>
                  <th className="text-center py-3 px-2 font-semibold whitespace-nowrap">Judging</th>
                  <th className="text-center py-3 px-2 font-semibold whitespace-nowrap w-[130px]">Round 1</th>
                  <th className="text-center py-3 px-2 font-semibold whitespace-nowrap w-[130px]">Round 2</th>
                  <th className="text-center py-3 px-2 font-semibold w-[150px]">Actions</th>
                </tr>
                {/* Filter row */}
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="w-8"></th>
                  <th className="py-2 px-2">
                    <Input
                      placeholder="Filter by name..."
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="grade-name-filter"
                    />
                  </th>
                  <th className="py-2 px-2">
                    <Select value={scheduleFilter} onValueChange={setScheduleFilter}>
                      <SelectTrigger className="h-8 text-sm" data-testid="grade-schedule-filter">
                        <SelectValue placeholder="All schedules" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All schedules</SelectItem>
                        {schedules.map(s => (
                          <SelectItem key={s.schedule_id} value={s.schedule_id}>
                            {s.schedule_type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </th>
                  <th></th>
                  <th className="py-2 px-2">
                    <Select value={judgingFilter} onValueChange={setJudgingFilter}>
                      <SelectTrigger className="h-8 text-sm" data-testid="grade-judging-filter">
                        <SelectValue placeholder="All judging" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All judging</SelectItem>
                        <SelectItem value="RollArt">RollArt</SelectItem>
                        <SelectItem value="White System">White System</SelectItem>
                      </SelectContent>
                    </Select>
                  </th>
                  <th></th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <SortableContext items={filteredGrades.map(g => g.grade_id)} strategy={verticalListSortingStrategy}>
                  {filteredGrades.map(grade => {
                    const discipline = disciplines.find(d => d.discipline_id === grade.discipline_id);
                    const schedule = schedules.find(s => s.schedule_id === grade.schedule_id);
                    const displayName = `${grade.name} ${discipline?.discipline || ''}`.trim();
                    const canEdit = canEditItem(grade);
                    return (
                      <SortableItem key={grade.grade_id} id={grade.grade_id} disabled={!isSuperUser}>
                        <td className="py-3 px-2 font-medium">{displayName}</td>
                        <td className="py-3 px-2 text-sm">{schedule?.schedule_type || '-'}</td>
                        <td className="py-3 px-2 text-center text-sm">{classificationLabel(grade.classification)}</td>
                        <td className="py-3 px-2 text-center text-sm whitespace-nowrap" data-testid={`grade-judging-system-${grade.grade_id}`}>{grade.judging_system || 'RollArt'}</td>
                        <td className="py-3 px-2 text-center text-sm whitespace-nowrap">{grade.round_one_content_type}</td>
                        <td className="py-3 px-2 text-center text-sm whitespace-nowrap">{grade.round_two_content_type}</td>
                        <td className="py-3 px-2">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={async () => { await handleEdit(grade); setViewOnly(true); }} className="action-icon" title="View" data-testid={`view-grade-${grade.grade_id}`}>
                              <Eye className="h-4 w-4" />
                            </button>
                            {canEdit && (
                              <>
                                <button onClick={() => handleEdit(grade)} className="action-icon" title="Edit" data-testid={`edit-grade-${grade.grade_id}`}>
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button onClick={() => handleDeleteClick(grade.grade_id)} className="action-icon text-red-600" title="Delete" data-testid={`delete-grade-${grade.grade_id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </SortableItem>
                    );
                  })}
                </SortableContext>
              </tbody>
            </table>
            {grades.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No grades yet. Create your first one!
              </div>
            )}
          </div>
        </DndContext>
      </Card>
      
      <AddGradeDialog 
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        sharedData={sharedData}
        setSharedData={setSharedData}
        gradeForms={gradeForms}
        disciplines={disciplines}
        schedules={schedules}
        figures={figures}
        dances={dances}
        updateGradeForm={updateGradeForm}
        updateGradeFormGroup={updateGradeFormGroup}
        formatFigureDisplay={formatFigureDisplay}
        handleAddSubmit={handleAddSubmit}
        resetAddForm={resetAddForm}
        currentUser={currentUser}
      />
      
      <EditGradeDialog 
        open={editDialogOpen}
        onOpenChange={(v) => { setEditDialogOpen(v); if (!v) setViewOnly(false); }}
        editingGrade={editingGrade}
        sharedData={sharedData}
        setSharedData={setSharedData}
        gradeForms={gradeForms}
        disciplines={disciplines}
        schedules={schedules}
        figures={figures}
        dances={dances}
        updateGradeForm={updateGradeForm}
        updateGradeFormGroup={updateGradeFormGroup}
        formatFigureDisplay={formatFigureDisplay}
        handleEditSubmit={handleEditSubmit}
        resetAddForm={resetAddForm}
        setEditingGrade={setEditingGrade}
        currentUser={currentUser}
        onTimingChange={setPendingTimingUpdate}
        readOnly={viewOnly}
      />
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.name ? `"${itemToDelete.name}"` : 'this grade'}
        loading={deleting}
      />

      <GradePickerDialog
        open={appendPickerOpen}
        onOpenChange={setAppendPickerOpen}
        mode="append"
        targetScheduleId={scheduleFilter}
        onCompleted={() => fetchGrades()}
      />
    </>
  );
}
